import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Button, Typography, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Accordion, AccordionSummary, AccordionDetails, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useMutation, useQuery, useSubscription } from '@apollo/client';
import { UPDATE_DATA_CHANNEL_CHECK } from '../graphql/mutations';
import { SUBSCRIBE_TO_FIXTURE_CHANNEL } from '../graphql/subscription';
import { GET_LIVE_FIXTURES } from '../graphql/queries';
import SwitchComponent from './SwitchComponent';
import { useAuth } from '../AuthContext';

const TogglePage = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const token = localStorage.getItem('token');
  const username = localStorage.getItem('username');
  if (!token || !username) navigate('/');
  let selectedFixture = JSON.parse(localStorage.getItem('selectedFixture'));
  if (!selectedFixture) navigate('/dropdownpage');

  const [currentFixture, setCurrentFixture] = useState(selectedFixture);
  const [dataChannels, setDataChannels] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');
  const [newValue, setNewValue] = useState(false);

  // Match Buddy - separate state (catName 11 = Category A, catName 12 = Category B)
  const MATCH_BUDDY_CAT_NAMES = [11, 12];
  const [matchBuddyData, setMatchBuddyData] = useState({});
  const matchBuddyInfoText = {
    full_data_removal: 'This will switch off all stats. This should be used in cases where a large volume of EFIs and Match score line for example are incorrect.',
  };

  const liveFixtures = useQuery(GET_LIVE_FIXTURES, { variables: { token }, fetchPolicy: 'no-cache' });

  useEffect(() => {
    if (liveFixtures.data) {
      const fixture = liveFixtures.data.liveFixtures.find((f) => f.aetFixtureId === selectedFixture.aetFixtureId);
      setCurrentFixture(fixture);
      localStorage.setItem('selectedFixture', JSON.stringify(fixture));
      const dataEndpoints = {};
      fixture.aecheckdatasource?.forEach((dataChannel) => {
        dataEndpoints[dataChannel.catName] = {
          label: dataChannel.catLabel,
          enabled: dataChannel.enabled,
          subCatList: dataChannel.subCatList?.reduce((acc, subCat) => {
            acc[subCat.catName] = { label: subCat.catLabel, enabled: subCat.enabled };
            return acc;
          }, {})
        };
      });
      setDataChannels(dataEndpoints);
      const matchBuddyEndpoints = {};
      fixture.aecheckdatasource?.forEach((dataChannel) => {
        if (MATCH_BUDDY_CAT_NAMES.includes(dataChannel.catName)) {
          matchBuddyEndpoints[dataChannel.catName] = {
            label: dataChannel.catLabel,
            enabled: dataChannel.enabled,
            subCatList: dataChannel.subCatList?.reduce((acc, subCat) => {
              acc[subCat.catName] = { label: subCat.catLabel, enabled: subCat.enabled };
              return acc;
            }, {})
          };
        }
      });
      setMatchBuddyData(matchBuddyEndpoints);
    }
    if (liveFixtures.error) {
      logout();
    }
  }, [liveFixtures]);

  const [update] = useMutation(UPDATE_DATA_CHANNEL_CHECK);
  const fixtureSubscription = useSubscription(SUBSCRIBE_TO_FIXTURE_CHANNEL, {
    variables: { aetFixtureId: selectedFixture.aetFixtureId, extFixtureId: selectedFixture.extFixtureId }
  });

  useEffect(() => {
    if (fixtureSubscription.data) {
      const { fixtureMetricChannel } = fixtureSubscription.data;

      const newChannels = {};
      fixtureMetricChannel.aecheckdatasource.forEach((dataChannel) => {
        newChannels[dataChannel.catName] = {
          label: dataChannel.catLabel,
          enabled: dataChannel.enabled,
          subCatList: dataChannel.subCatList?.reduce((acc, subCat) => {
            acc[subCat.catName] = { label: subCat.catLabel, enabled: subCat.enabled };
            return acc;
          }, {}),
        };
      });

      // Merge the subscription data with the existing state
      setDataChannels((prevDataChannels) => ({ ...prevDataChannels, ...newChannels }));

      // Merge Match Buddy subscription data separately
      const matchBuddyUpdates = {};
      fixtureMetricChannel.aecheckdatasource.forEach((dataChannel) => {
        if (MATCH_BUDDY_CAT_NAMES.includes(dataChannel.catName)) {
          matchBuddyUpdates[dataChannel.catName] = {
            label: dataChannel.catLabel,
            enabled: dataChannel.enabled,
            subCatList: dataChannel.subCatList?.reduce((acc, subCat) => {
              acc[subCat.catName] = { label: subCat.catLabel, enabled: subCat.enabled };
              return acc;
            }, {}),
          };
        }
      });
      if (Object.keys(matchBuddyUpdates).length > 0) {
        setMatchBuddyData((prev) => ({ ...prev, ...matchBuddyUpdates }));
      }
    }
  }, [fixtureSubscription.data]);


  const handleToggleChange = (title, label, enabled, isMainCategory = false) => {
    setSelectedTitle(title);
    setSelectedLabel(label);
    setNewValue(!enabled);
    setOpenDialog(true);

    // If it's a main category, we might also want to update subcategories at the same time
    if (isMainCategory) {
      // If the main category is toggled off, we should turn off all subcategories as well
      if (!enabled) {
        // Update all subcategories for the main category
        const subCatList = dataChannels[title]?.subCatList;
        if (subCatList) {
          Object.keys(subCatList).forEach((subCat) => {
            subCatList[subCat].enabled = false;
          });
        }
      }
    }
  };


  const handleConfirm = () => {
    // Update data channel on the backend
    updateDataChannel(selectedTitle, newValue);
  
    // Update the local state for dataChannels
    setDataChannels((prevDataChannels) => {
      const updatedChannels = { ...prevDataChannels };
  
      // Case 1: If it's a main category
      if (updatedChannels[selectedTitle]) {
        updatedChannels[selectedTitle] = {
          ...updatedChannels[selectedTitle],
          enabled: newValue,
        };
  
        // Toggle all subcategories based on main category toggle
        if (updatedChannels[selectedTitle].subCatList) {
          updatedChannels[selectedTitle].subCatList = Object.keys(
            updatedChannels[selectedTitle].subCatList
          ).reduce((acc, subCat) => {
            acc[subCat] = {
              ...updatedChannels[selectedTitle].subCatList[subCat],
              enabled: newValue,
            };
            return acc;
          }, {});
        }
      }
  
      // Case 2: If it's a subcategory
      else {
        Object.keys(updatedChannels).forEach((mainCat) => {
          const subCatList = updatedChannels[mainCat].subCatList;
          if (subCatList && subCatList[selectedTitle]) {
            // Update the toggled subcategory
            subCatList[selectedTitle].enabled = newValue;
  
            // Check if any subcategory is enabled
            const anyEnabled = Object.values(subCatList).some(
              (sub) => sub.enabled
            );
  
            // Sync the main category enabled state
            updatedChannels[mainCat].enabled = anyEnabled;
          }
        });
      }
  
      return updatedChannels;
    });
  
    // Update Match Buddy state if the toggle belongs to Match Buddy
    setMatchBuddyData((prevMatchBuddy) => {
      const updated = { ...prevMatchBuddy };

      // Case 1: Main Match Buddy category toggled (catName 11 or 12)
      if (updated[selectedTitle]) {
        updated[selectedTitle] = {
          ...updated[selectedTitle],
          enabled: newValue,
        };
        if (updated[selectedTitle].subCatList) {
          updated[selectedTitle].subCatList = Object.keys(
            updated[selectedTitle].subCatList
          ).reduce((acc, subCat) => {
            acc[subCat] = {
              ...updated[selectedTitle].subCatList[subCat],
              enabled: newValue,
            };
            return acc;
          }, {});
        }
      }
      // Case 2: Sub-category toggled
      else {
        Object.keys(updated).forEach((mainCat) => {
          const subCatList = updated[mainCat].subCatList;
          if (subCatList && subCatList[selectedTitle]) {
            subCatList[selectedTitle].enabled = newValue;
            const anyEnabled = Object.values(subCatList).some(
              (sub) => sub.enabled
            );
            updated[mainCat].enabled = anyEnabled;
          }
        });
      }

      return updated;
    });

    // Close the dialog after confirming
    setOpenDialog(false);
  };



  const handleCancel = () => setOpenDialog(false);

  const updateDataChannel = (catName, enabled) => {
    update({
      variables: {
        data: {
          aetFixtureId: selectedFixture.aetFixtureId,
          extFixtureId: selectedFixture.extFixtureId,
          catName,
          enabled,
          username,
          databaseName: 'aether-db-fifa'
        }
      }
    });
  };

  return (
    <Box sx={{ mt: '60px', overflowY: 'auto', height: '100vh', background: "black" }}>
      <Container maxWidth="xl" sx={{ display: 'flex', flexDirection: 'column', background: 'black', padding: '1rem' }}>
        <Typography variant="h5" gutterBottom sx={{
          textAlign: 'center', color: 'white', fontWeight: 'normal',
          fontFamily: 'Lato, sans-serif',
        }}>
          {currentFixture.competitionName}
        </Typography>
        <Container sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem', border: '1px solid #ccc',
          borderRadius: '8px', boxShadow: 3, backgroundColor: '#E8E8E8', maxHeight: '80vh', overflowY: 'auto'
        }}>
          <Typography variant="h6" gutterBottom sx={{
            textAlign: 'center',
            fontWeight: 'bold',
            fontFamily: 'Lato, sans-serif',
            mb: 2,
          }}>
            {currentFixture.fixtureName}
          </Typography>

          {Object.keys(dataChannels).filter((key) => !MATCH_BUDDY_CAT_NAMES.includes(Number(key))).map((title) => {
            const { enabled, label, subCatList } = dataChannels[title];

            return (
              <Accordion key={title} sx={{ width: '100%', marginBottom: '10px' }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${title}-content`}
                  id={`${title}-header`}
                >
                  <Typography  variant="h5"sx={{
                    textAlign: 'center',
                   
                    fontFamily: 'Lato, sans-serif',
                   
                  }}>{label}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <SwitchComponent
                    title={title}
                    enabled={enabled}
                    label={label}
                    onChange={() => handleToggleChange(title, label, enabled, true)}
                  />
                  {subCatList && Object.keys(subCatList).map((subCat) => (
                    <SwitchComponent
                      key={subCat}
                      title={subCat}
                      enabled={subCatList[subCat].enabled}
                      label={subCatList[subCat].label}
                      onChange={() =>
                        handleToggleChange(subCat, subCatList[subCat].label, subCatList[subCat].enabled)
                      }
                    />
                  ))}
                </AccordionDetails>
              </Accordion>
            );
          })}

          <Dialog open={openDialog} onClose={handleCancel}> 
            <DialogTitle>Confirm Toggle</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to {newValue ? 'enable' : 'disable'} {selectedLabel}?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancel} color="black">Cancel</Button>
              <Button onClick={handleConfirm} color="black">Confirm</Button>
            </DialogActions>
          </Dialog>
        </Container>
        {/* FIFA Match Buddy Section - separate from existing data channels */}
        {Object.keys(matchBuddyData).length > 0 && (
          <Container sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem', border: '1px solid #ccc',
            borderRadius: '8px', boxShadow: 3, backgroundColor: '#E8E8E8', maxHeight: '80vh', overflowY: 'auto',
            marginTop: '1.5rem',
          }}>
            <Typography variant="h6" gutterBottom sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              fontFamily: 'Lato, sans-serif',
              mb: 2,
            }}>
              FIFA MATCH BUDDY
            </Typography>

            {Object.entries(matchBuddyData).map(([catKey, channel]) => (
              <Accordion key={catKey} sx={{ width: '100%', marginBottom: '10px' }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`matchbuddy-${catKey}-content`}
                  id={`matchbuddy-${catKey}-header`}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h5" sx={{ fontFamily: 'Lato, sans-serif' }}>{channel.label}</Typography>
                    {channel.subCatList && Object.keys(channel.subCatList).some(
                      (subCat) => matchBuddyInfoText[subCat]
                    ) && (
                      <Tooltip
                        title={Object.keys(channel.subCatList)
                          .filter((subCat) => matchBuddyInfoText[subCat])
                          .map((subCat) => matchBuddyInfoText[subCat])
                          .join(' ')}
                        arrow
                        placement="top"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: '#333',
                              color: '#fff',
                              fontSize: '0.85rem',
                              maxWidth: 320,
                              padding: '10px 14px',
                            },
                          },
                        }}
                      >
                        <InfoOutlinedIcon sx={{ color: '#666', cursor: 'pointer', fontSize: '1.2rem' }} />
                      </Tooltip>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <SwitchComponent
                    title={catKey}
                    enabled={channel.enabled}
                    label={channel.label}
                    onChange={() => handleToggleChange(catKey, channel.label, channel.enabled, true)}
                  />
                  {channel.subCatList && Object.keys(channel.subCatList).map((subCat) => {
                    const sub = channel.subCatList[subCat];
                    return (
                      <SwitchComponent
                        key={subCat}
                        title={subCat}
                        enabled={sub.enabled}
                        label={sub.label}
                        onChange={() => handleToggleChange(subCat, sub.label, sub.enabled)}
                      />
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            ))}
          </Container>
        )}
      </Container>
    </Box>
  );
};

export default TogglePage;
